import React, { useState } from "react";
import Card from 'components/card';
import { 
  Button, 
  IconButton, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Box,
  Switch,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { DataGrid } from '@mui/x-data-grid';
import { useFetchData, useCreateData, useUpdateData, useDeleteData } from 'services/queries';
import { useAPI } from 'services/useAPI';
import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import PaymentTermForm from './PaymentTermForm';
import PaymentTermView from './PaymentTermView';
import ToolbarCrud from 'components/toolbar/ToolbarCrud';

const PaymentTermsSettings = () => {
  const queryClient = useQueryClient();
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Fetch payment term templates
  const { data: templates, isLoading } = useFetchData(
    'payment-terms/templates/',
    true
  );

  // Mutations
  const deleteMutation = useDeleteData(
    'payment-terms/templates',
    ['payment-terms/templates'],
    {
      showNotification: true,
      onSuccess: () => {
        setDeleteConfirmOpen(false);
        setTemplateToDelete(null);
      }
    }
  );

  const api = useAPI();

  const deactivateMutation = useMutation({
    mutationFn: async ({ id }) => {
      const { data } = await api.post(`payment-terms/templates/${id}/deactivate/`);
      return data;
    },
    onSuccess: () => {
      toast.success('Option deactivated successfully');
      queryClient.invalidateQueries(['payment-terms/templates']);
    },
    onError: (error) => {
      toast.error('Failed to deactivate template');
      console.error(error);
    }
  });

  const activateMutation = useMutation({
    mutationFn: async ({ id }) => {
      const { data } = await api.post(`payment-terms/templates/${id}/activate/`);
      return data;
    },
    onSuccess: () => {
      toast.success('Option activated successfully');
      queryClient.invalidateQueries(['payment-terms/templates']);
    },
    onError: (error) => {
      toast.error('Failed to activate template');
      console.error(error);
    }
  });

  const handleAdd = () => {
    setSelectedTemplate(null);
    setOpenForm(true);
  };

  const handleView = (template) => {
    setSelectedTemplate(template);
    setOpenView(true);
  };

  const handleDelete = (template) => {
    if (template.usage_count > 0) {
      toast.error(`Cannot delete option. It is being used by ${template.usage_count} order(s).`);
      return;
    }
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (templateToDelete) {
      deleteMutation.mutate(templateToDelete.id);
    }
  };

  const handleToggleActive = (template) => {
    if (template.is_active) {
      deactivateMutation.mutate({ id: template.id });
    } else {
      activateMutation.mutate({ id: template.id });
    }
  };

  const columns = [
    { 
      field: 'abbreviation', 
      headerName: 'Code', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <strong>{params.value}</strong>
        </Box>
      )
    },
    {
      field: 'name',
      headerName: 'Description',
      flex: 1,
      minWidth: 350,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            icon={params.value ? <CheckCircleIcon /> : <CancelIcon />}
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        </Box>
      )
    },
    {
      field: 'usage_count',
      headerName: 'Usage',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value || 0}
          </Typography>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleView(params.row)}
            title="View"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <Switch
            checked={params.row.is_active}
            onChange={() => handleToggleActive(params.row)}
            size="small"
            title={params.row.is_active ? 'Deactivate' : 'Activate'}
          />
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            title="Delete"
            disabled={params.row.usage_count > 0}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  // Handle both paginated and non-paginated responses
  const rows = React.useMemo(() => {
    if (!templates) return [];
    // If it's an array, use it directly
    if (Array.isArray(templates)) {
      return templates;
    }
    // If it has a results property (paginated), use that
    if (templates.results && Array.isArray(templates.results)) {
      return templates.results;
    }
    // Otherwise return empty array
    return [];
  }, [templates]);

  return (
    <div className="flex flex-col gap-5">
      <div className="mt-5 grid h-full">
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          Payment Term Options
        </h1>
      </div>
      
      <Card extra={'w-full h-full bg-white p-4'}>
        {rows.length === 0 && !isLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No payment term options created yet. Click "Add New Option" to create your first option.
          </Alert>
        )}
        
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={isLoading}
            autoHeight
            rowHeight={60}
            slots={{
              toolbar: () => (
                <Box sx={{ mb: 2 }}>
                  <ToolbarCrud
                    resource="payment-terms"
                    name="Payment Term Option"
                    onCreateClick={handleAdd}
                  />
                </Box>
              )
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e0e0e0',
              }
            }}
          />
        </Box>
      </Card>

      {/* Form Dialog */}
      {openForm && (
        <PaymentTermForm
          open={openForm}
          onClose={() => setOpenForm(false)}
          template={selectedTemplate}
          onSuccess={() => {
            setOpenForm(false);
            // Query invalidation is handled by the mutation in PaymentTermForm
          }}
        />
      )}

      {/* View Dialog */}
      {openView && (
        <PaymentTermView
          open={openView}
          onClose={() => setOpenView(false)}
          template={selectedTemplate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the option "{templateToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PaymentTermsSettings;